import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MaterialAutosuggestComponent } from '../../../material-autosuggest/material-autosuggest.component';
import { MaterialLookup, MaterialSearchResult } from '../../../../services/lookup.service';
import { formatFixedNumber, toFiniteNumber } from '../../utils/slurry-material-calculation.helpers';
import { getCisProContainerTooltip, hasDisposedCisProContainer } from '../../utils/slurry-cispro-disposed.helpers';

export interface SlurryLiquidMaterialRow {
  liquid: string;
  volumeGal: number;
  specificGravity: number;
  weightLb: number;
  labWeightG: number;
  cisPRO: string;
}

export interface SlurryLiquidAdditiveRow {
  additive: string;
  name?: string;
  volumeGal: number;
  specificGravity: number;
  weightLb: number;
  labWeightG: number;
  cisPRO: string;
}

interface EditableNumberInputChange {
  target: Record<string, unknown>;
  key: string;
  value: unknown;
}

interface EditableNumberBlur {
  target: Record<string, unknown>;
  key: string;
  decimals: number;
}

@Component({
  selector: 'app-slurry-liquid-materials-section',
  standalone: true,
  imports: [CommonModule, FormsModule, MaterialAutosuggestComponent],
  templateUrl: './slurry-liquid-materials-section.component.html'
})
export class SlurryLiquidMaterialsSectionComponent {
  @Input() liquidMaterials: SlurryLiquidMaterialRow[] = [];
  @Input() liquidAdditives: SlurryLiquidAdditiveRow[] = [];
  @Input() liquidBaseMaterials: MaterialLookup[] = [];
  @Input() liquidBaseValidationError = '';
  @Input() liquidAdditiveExcludeIds: number[] = [];
  @Input() isEditable = false;
  @Input() disposedContainerIds: ReadonlySet<string> = new Set<string>();

  @Output() liquidMaterialChanged = new EventEmitter<number>();
  @Output() recalculateRequested = new EventEmitter<void>();
  @Output() liquidBaseCisProSearchRequested = new EventEmitter<number>();
  @Output() liquidAdditiveCisProSearchRequested = new EventEmitter<number>();
  @Output() liquidAdditiveSelected = new EventEmitter<MaterialSearchResult>();
  @Output() liquidAdditiveRemoved = new EventEmitter<number>();

  private activeEditableNumberTarget: Record<string, unknown> | null = null;
  private activeEditableNumberKey: string | null = null;
  private activeEditableNumberRawValue: unknown = null;

  fmt(value: unknown, decimals: number): string {
    return formatFixedNumber(value, decimals);
  }

  isDisposedContainer(value: string | null | undefined): boolean {
    return hasDisposedCisProContainer(value, this.disposedContainerIds);
  }

  containerTooltip(value: string | null | undefined): string {
    return getCisProContainerTooltip(value, this.disposedContainerIds, true);
  }

  editableNumberValue(target: Record<string, unknown>, key: string, decimals: number): string {
    if (this.activeEditableNumberTarget === target && this.activeEditableNumberKey === key) {
      return `${this.activeEditableNumberRawValue ?? target?.[key] ?? ''}`;
    }

    return this.fmt(target?.[key], decimals);
  }

  focusEditableNumber(event: FocusEvent, target: Record<string, unknown>, key: string): void {
    this.activeEditableNumberTarget = target;
    this.activeEditableNumberKey = key;
    this.activeEditableNumberRawValue = target?.[key] ?? '';
    const input = event.target as HTMLInputElement;
    input.select();
  }

  onEditableNumberInput(change: EditableNumberInputChange): void {
    change.target[change.key] = change.value;
    if (this.activeEditableNumberTarget === change.target && this.activeEditableNumberKey === change.key) {
      this.activeEditableNumberRawValue = change.value;
    }
    this.recalculateRequested.emit();
  }

  formatNumberOnBlur(change: EditableNumberBlur): void {
    const value = toFiniteNumber(change.target[change.key]);
    change.target[change.key] = value;
    this.recalculateRequested.emit();
    change.target[change.key] = value.toFixed(change.decimals);
    this.clearEditableNumberFocus(change.target, change.key);
  }

  private clearEditableNumberFocus(target?: Record<string, unknown>, key?: string): void {
    if (!target || !key || (this.activeEditableNumberTarget === target && this.activeEditableNumberKey === key)) {
      this.activeEditableNumberTarget = null;
      this.activeEditableNumberKey = null;
      this.activeEditableNumberRawValue = null;
    }
  }
}
